import React, { useContext, useEffect, useState } from 'react';
import Table from 'react-bootstrap/Table';
import Pagination from 'react-bootstrap/Pagination';
import { FaArrowUpShortWide, FaArrowDownWideShort } from 'react-icons/fa6';
import { Form, InputGroup } from 'react-bootstrap';
import { Button } from '@/shared/components/Button';
import toast from 'react-hot-toast';
import { GlobalContext } from '../../../../../../context/GlobalContext';
import { AuthContext } from '../../../../../../context/AuthContext';
import { SideBarRefreshContext } from '../../../../../../context/SideBarRefreshContext';

const DataReactTable = () => {
  const [approvedData, setApprovedData] = useState([]);
  const { apiGlobal } = useContext(GlobalContext);
  const { agentId, countryId } = useContext(AuthContext);
  const [filteredApprovedData, setFilteredApprovedData] = useState([]);
  const [searchStartDate, setSearchStartDate] = useState('');
  const [refresh, setRefresh] = useState(false);
  const { sideBarRefresh } = useContext(SideBarRefreshContext);
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-');
    return `${day}-${month}-${year}`;
  };

  const fetchPendingData = async () => {
    const formData = new FormData();
    formData.append('agent_id', agentId);
    formData.append('country_id', countryId);
    try {
      const url = await fetch(`${apiGlobal}/agent/account_approved_list.php`, {
        method: 'POST',
        body: formData,
      });
      const data = await url.json();
      if (data.status && data.message === 'Success') {
        setApprovedData(data.data);
      } else {
        setApprovedData([]);
        console.error('Error fetching payment approved list');
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchPendingData();
  }, [apiGlobal, agentId, refresh, sideBarRefresh?.agentApprovedRefresh]);

  // Filter Data Based on Date Range
  useEffect(() => {
    let filteredData = [...approvedData];

    if (searchStartDate) {
      filteredData = filteredData.filter((trip) => {
        const tripPaymentDate = new Date(trip.payment_date); // ✅ FIXED
        return tripPaymentDate.getTime() === new Date(searchStartDate).getTime();
      });
    }

    // if (searchEndDate) {
    //   filteredData = filteredData.filter((trip) => {
    //     const tripEndDate = new Date(trip.end_date);
    //     return tripEndDate <= new Date(searchEndDate);
    //   });
    // }

    setFilteredApprovedData(filteredData);
    setCurrentPage(1); // Reset to first page on filter change
  }, [searchStartDate, approvedData]);

  // Pagination Logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredApprovedData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredApprovedData.length / itemsPerPage);

  const [sortOrder, setSortOrder] = useState('asc');
  const [sortColumn, setSortColumn] = useState('payment_date'); // This will track the column being sorted
   
  const handleSort = (column) => {
    const newSortOrder = sortColumn === column && sortOrder === 'asc' ? 'desc' : 'asc';
  
    const sortedData = [...filteredApprovedData].sort((a, b) => {
      if (column === 'payment_date') {
        return newSortOrder === 'asc'
          ? new Date(a.payment_date) - new Date(b.payment_date)
          : new Date(b.payment_date) - new Date(a.payment_date);
          // eslint-disable-next-line
      } else if (column === 'balance') {
        return newSortOrder === 'asc'
          ? parseFloat(a.balance) - parseFloat(b.balance)
          : parseFloat(b.balance) - parseFloat(a.balance);
      } else if (column === 'status') {
        return newSortOrder === 'asc' ? a.status.localeCompare(b.status) : b.status.localeCompare(a.status);
      }
      return 0;
    });
  
    setFilteredApprovedData(sortedData); // Ensure UI updates correctly
    setSortOrder(newSortOrder);
    setSortColumn(column);
  };

  return (
    <div>
      {/* Search Filters */}
      <div
        className="mb-4"
        style={{
          padding: '15px',
          borderRadius: '8px',
          backgroundColor: '#f8f9fa',
          display: 'flex',
          justifyContent: 'flex-start',
          gap: '10px',
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <InputGroup className="mb-2" style={{ maxWidth: '160px' }}>
          <Form.Control
            type="date"
            placeholder="Start Date"
            value={searchStartDate}
            onChange={e => setSearchStartDate(e.target.value)}
            style={{ borderRadius: '25px', padding: '8px', border: '1px solid #ddd' }}
          />
        </InputGroup>


        <Button
          variant="outline-danger"
          style={{ 
            borderRadius: '25px', 
            padding: '8px 16px', 
            marginLeft: '10px', 
            alignSelf: 'center',
           }}
          className="mt-3"
          onClick={() => {
            setSearchStartDate('');
          }}
        >
          Clear Filters
        </Button>
        <Button
          variant="outline-danger"
          style={{
            borderRadius: '25px',
            padding: '8px 16px',
            marginLeft: '2px',
            alignSelf: 'center',
          }}
          className="mt-3"
          onClick={() => {
            setRefresh(prev => !prev);
            toast.success('Data Refreshed Successfully');
          }}
        >
          Refresh
        </Button>
      </div>

      {/* Table */}
      <Table
        responsive
        style={{
          backgroundColor: '#f9f9f9',
          borderRadius: '8px',
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
        }}
      >
        <thead className="bg-success" style={{ color: 'white', textAlign: 'center' }}>
          <tr>
            <th>#</th>
            <th onClick={() => handleSort('payment_date')} style={{ cursor: 'pointer' }}>
              Payment Date {sortColumn === 'payment_date'
               && (sortOrder === 'asc' ? <FaArrowUpShortWide /> : <FaArrowDownWideShort />)}
            </th>
            <th onClick={() => handleSort('balance')} style={{ cursor: 'pointer' }}>
              Booking Amount {sortColumn === 'balance' 
              && (sortOrder === 'asc' ? <FaArrowUpShortWide /> : <FaArrowDownWideShort />)}
            </th>
            <th>Status</th>
            {/* <th>Action</th> */}
          </tr>
        </thead>
        <tbody>
          {filteredApprovedData.length === 0 ? (
            <tr>
              <td colSpan="7" style={{ textAlign: 'center', padding: '20px' }}>
                <h3>No Approved Data Available</h3>
              </td>
            </tr>
          ) : (
            currentItems.map((booking, index) => (
              <tr
                key={booking.id}
                style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#f2f2f2', textAlign: 'center' }}
              >
                <td>{index + 1 + (currentPage - 1) * itemsPerPage}</td>
                <td>{formatDate(booking.payment_date)}</td>
                <td>{booking.balance}</td>
                <td>
                  <span style={{ color: 'green' }}>{booking.status === '1' && 'Approved'}</span>
                </td>
                {/* <td><Button type="button" variant="danger"><FaTrash /></Button></td> */}
              </tr>
            ))
          )}
        </tbody>
      </Table>

      {/* Pagination Controls */}
      {/* {filteredApprovedData.length > 0 && totalPages > 1 && ( */}
      <div
        style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          zIndex: 999,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Pagination>
          <Pagination.Prev 
            onClick={() => setCurrentPage(currentPage - 1)} 
            disabled={currentPage === 1}
          />
          {Array.from({ length: totalPages }, (_, index) => (
            <Pagination.Item
              key={index + 1}
              active={index + 1 === currentPage}
              onClick={() => setCurrentPage(index + 1)}
            >
              {index + 1}
            </Pagination.Item>
            ))}
          <Pagination.Next 
            onClick={() => setCurrentPage(currentPage + 1)} 
            disabled={currentPage === totalPages}
          />
        </Pagination>
      </div>
      {/* )
      } */}
    </div>
  );
};
 
export default DataReactTable;

